import { aiContentTypeLabel } from '../model/presentation';
import { AiContentHeader } from './AiContentHeader';
import { useRef, useState } from 'react';
import { useBlocker } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  helpdeskAiContentApi,
  isApiError,
  type AiContentDetailResponse,
  type AiContentPatch,
  type AiContentSaveResponse,
  type HelpdeskAiContentKind,
} from '@shared/api';
import {
  Alert,
  AlertTitle,
  AlertDescription,
  Button,
  Card,
  CardContent,
  CardFooter,
  ConfirmDialog,
  DescriptionList,
  Heading,
  Input,
  Label,
  RequiredMark,
  PageBackLink,
  PopoverSelect,
  Text,
  Textarea,
} from '@shared/ui';
import { aiContentFormOptionsQuery, aiContentKeys } from '../api/queries';
import { changedFields, errorKey, outcomeUnknown, toDraft } from '../model/editor';

type Intent = { body: AiContentPatch; etag: string; key: string };
export function AiContentEditor({
  kind,
  scope,
  initial,
  onCancel,
  onBack,
  onSaved,
  canUpdate = true,
}: {
  canUpdate?: boolean;
  kind: HelpdeskAiContentKind;
  scope: string;
  initial: AiContentDetailResponse;
  onCancel: () => void;
  onBack: () => void;
  onSaved: (detail: AiContentDetailResponse, receipt: AiContentSaveResponse) => void;
}) {
  const { t } = useTranslation();
  const options = useQuery(aiContentFormOptionsQuery(scope, kind));
  const client = useQueryClient();
  const [base, setBase] = useState(initial);
  const [draft, setDraft] = useState(() => toDraft(initial));
  const [latest, setLatest] = useState<AiContentDetailResponse>();
  const [error, setError] = useState<unknown>();
  const [readError, setReadError] = useState<unknown>();
  const [invalid, setInvalid] = useState<{ title?: boolean; content?: boolean; type?: boolean }>(
    {}
  );
  const [uncertain, setUncertain] = useState(false);
  const [receipt, setReceipt] = useState<AiContentSaveResponse>();
  const [loadingLatest, setLoadingLatest] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const intent = useRef<Intent | null>(null);
  const saving = useRef(false);
  const acknowledged = useRef<AiContentSaveResponse | null>(null);
  const allowLeave = useRef(false);
  const form = useRef<HTMLFormElement>(null);
  const changes = changedFields(draft, toDraft(base));
  const dirty = Object.keys(changes).length > 0;
  const mutation = useMutation({
    mutationFn: (request: Intent) =>
      helpdeskAiContentApi.update(kind, initial.data.id, request.body, request.etag, request.key),
    retry: false,
  });
  const blocker = useBlocker({
    shouldBlockFn: () =>
      !allowLeave.current && !acknowledged.current && (dirty || uncertain || saving.current),
    enableBeforeUnload: !receipt && (dirty || uncertain),
    withResolver: true,
  });
  const busy = submitting || mutation.isPending || loadingLatest;
  const denied = isApiError(error) && [401, 403, 404].includes(error.status);
  // Only a failed version precondition can be resolved by rebasing the draft.
  // Delivery, identity and retained-operation fences must remain server-owned.
  const conflict = isApiError(error) && [412, 428].includes(error.status);
  const locked = busy || uncertain || Boolean(receipt);
  async function saveIntent(request: Intent): Promise<AiContentSaveResponse> {
    try {
      return await mutation.mutateAsync(request);
    } catch (failure) {
      // A known rejected precondition has made no write. Refresh once and use the
      // new complete validator only when all business fields are still unchanged.
      // Never renew an uncertain request or retry a second concurrent change.
      if (uncertain || !isApiError(failure) || failure.status !== 412) throw failure;
      let current: AiContentDetailResponse;
      try {
        current = await helpdeskAiContentApi.detail(kind, initial.data.id);
      } catch (readFailure) {
        if (isApiError(readFailure) && [401, 403, 404].includes(readFailure.status))
          throw readFailure;
        throw failure;
      }
      if (
        current.data.id !== base.data.id ||
        current.data.title !== base.data.title ||
        current.data.content !== base.data.content ||
        current.data.type !== base.data.type ||
        current.data.deletion ||
        current.etag === request.etag
      )
        throw failure;
      const refreshed = { ...request, etag: current.etag, key: crypto.randomUUID() };
      intent.current = refreshed;
      setBase(current);
      return mutation.mutateAsync(refreshed);
    }
  }
  async function readSaved(saved: AiContentSaveResponse) {
    setLoadingLatest(true);
    try {
      const detail = await helpdeskAiContentApi.detail(kind, initial.data.id);
      client.setQueryData(aiContentKeys.detail(scope, kind, initial.data.id), detail);
      void client.invalidateQueries({ queryKey: aiContentKeys.lists(scope, kind) });
      allowLeave.current = true;
      onSaved(detail, saved);
    } catch (e) {
      setReadError(e);
    } finally {
      setLoadingLatest(false);
    }
  }
  async function submit() {
    if (
      !canUpdate ||
      saving.current ||
      acknowledged.current ||
      loadingLatest ||
      (denied && !uncertain) ||
      latest ||
      (conflict && !uncertain)
    )
      return;
    if (!intent.current) {
      if (!dirty) return;
      const fields = {
        type:
          changes.type !== undefined &&
          ((kind === 'instructions' && !changes.type) ||
            Boolean(
              changes.type && !options.data?.types.some((item) => item.value === changes.type)
            )),
        title:
          changes.title !== undefined &&
          (!changes.title || changes.title.length > 500 || changes.title.includes('\u0000')),
        content:
          changes.content !== undefined &&
          (!changes.content ||
            changes.content.length > 100000 ||
            changes.content.includes('\u0000')),
      };
      setInvalid(fields);
      if (fields.title || fields.content || fields.type) {
        form.current
          ?.querySelector<HTMLElement>(
            fields.title
              ? '#ai-content-title'
              : fields.type
                ? '#ai-content-type'
                : '#ai-content-body'
          )
          ?.focus();
        return;
      }
      intent.current = { body: changes, etag: base.etag, key: crypto.randomUUID() };
    }
    saving.current = true;
    setSubmitting(true);
    setError(undefined);
    setReadError(undefined);
    try {
      const saved = await saveIntent(intent.current);
      acknowledged.current = saved;
      setReceipt(saved);
      setUncertain(false);
      toast.success(t('aiContent.save.saved'));
      await readSaved(saved);
    } catch (e) {
      setError(e);
      const unknown = uncertain || outcomeUnknown(e);
      setUncertain(unknown);
      if (!unknown) intent.current = null;
    } finally {
      saving.current = false;
      setSubmitting(false);
    }
  }
  async function refresh() {
    if (busy) return;
    setLoadingLatest(true);
    setReadError(undefined);
    try {
      setLatest(await helpdeskAiContentApi.detail(kind, initial.data.id));
    } catch (e) {
      setReadError(e);
    } finally {
      setLoadingLatest(false);
    }
  }
  function chooseLatest(keepDraft: boolean) {
    if (!latest) return;
    const retained = keepDraft ? changedFields(draft, toDraft(base)) : {};
    setDraft({
      ...toDraft(latest),
      ...retained,
      type: retained.type !== undefined ? (retained.type ?? '') : (latest.data.type ?? ''),
    });
    setBase(latest);
    setLatest(undefined);
    setError(undefined);
    setReadError(undefined);
    setInvalid({});
    intent.current = null;
  }
  function cancel() {
    allowLeave.current = true;
    onCancel();
  }
  const actualChange =
    latest &&
    (latest.data.title !== base.data.title ||
      latest.data.content !== base.data.content ||
      latest.data.type !== base.data.type);
  const fieldErrors = {
    title: invalid.title
      ? !draft.title.trim()
        ? t('common.validation.required')
        : draft.title.trim().length > 500
          ? t('common.validation.maxLength', { max: 500 })
          : t('common.validation.unsupportedCharacters')
      : undefined,
    content: invalid.content
      ? !draft.content
        ? t('common.validation.required')
        : draft.content.length > 100000
          ? t('common.validation.maxLength', { max: 100000 })
          : t('common.validation.unsupportedCharacters')
      : undefined,
    type: invalid.type
      ? t(draft.type ? 'common.validation.choice' : 'common.validation.required')
      : undefined,
  };
  // One contextual recovery surface; transport uncertainty is never treated as a new intent.
  const feedback = receipt
    ? readError
      ? 'saved'
      : undefined
    : uncertain
      ? 'unknown'
      : latest
        ? 'latest'
        : !canUpdate
          ? 'permission'
          : error
            ? 'error'
            : undefined;
  return (
    <section className="flex min-w-0 flex-col gap-4" aria-label={t('aiContent.edit')}>
      <PageBackLink onClick={onBack}>{t(`aiContent.back.${kind}`)}</PageBackLink>
      <AiContentHeader
        record={base.data}
        typeLabel={aiContentTypeLabel(base.data.type, options.data, t('crm.missingValue'))}
      />
      <Card>
        <form
          ref={form}
          noValidate
          onSubmit={(e) => {
            e.preventDefault();
            if (receipt) void readSaved(receipt);
            else void submit();
          }}
        >
          <CardContent className="min-w-0 space-y-4 p-4">
            <Heading as="h2" size="md">
              {t('aiContent.information')}
            </Heading>
            <div className="space-y-1">
              <Label htmlFor="ai-content-title">
                {t('aiContent.fields.title')}
                <RequiredMark />
              </Label>
              <Input
                id="ai-content-title"
                aria-required="true"
                required
                value={draft.title}
                maxLength={500}
                disabled={locked}
                aria-invalid={invalid.title || undefined}
                aria-describedby={invalid.title ? 'ai-content-title-error' : undefined}
                onChange={(e) => {
                  setDraft({ ...draft, title: e.target.value });
                  setInvalid({ ...invalid, title: false });
                }}
              />
              {fieldErrors.title ? (
                <Text id="ai-content-title-error" role="alert" size="sm" color="destructive">
                  {fieldErrors.title}
                </Text>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label htmlFor="ai-content-type">
                {t('aiContent.fields.type')}
                {kind === 'instructions' ? <RequiredMark /> : null}
              </Label>
              <PopoverSelect
                id="ai-content-type"
                triggerAriaLabel={t('aiContent.fields.type')}
                value={draft.type}
                required={kind === 'instructions'}
                disabled={locked || (kind === 'instructions' && Boolean(options.error))}
                loading={options.isPending}
                loadingLabel={t('common.loading')}
                emptyText={t('aiContent.create.noTypes')}
                options={[
                  ...(kind === 'prompts' ? [{ value: '', label: t('crm.missingValue') }] : []),
                  ...(base.data.type &&
                  !options.data?.types.some((item) => item.value === base.data.type)
                    ? [
                        {
                          value: base.data.type,
                          label: aiContentTypeLabel(
                            base.data.type,
                            options.data,
                            t('crm.missingValue')
                          ),
                        },
                      ]
                    : []),
                  ...(options.data?.types ?? []),
                ]}
                aria-invalid={invalid.type || undefined}
                aria-describedby={
                  [
                    invalid.type && 'ai-content-type-validation',
                    options.error && 'ai-content-type-error',
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
                onValueChange={(type) => {
                  setDraft({ ...draft, type });
                  setInvalid({ ...invalid, type: false });
                }}
              />
              {fieldErrors.type ? (
                <Text id="ai-content-type-validation" role="alert" size="sm" color="destructive">
                  {fieldErrors.type}
                </Text>
              ) : null}
              {options.error ? (
                <Alert id="ai-content-type-error" variant="destructive">
                  <AlertTitle>{t(errorKey(options.error))}</AlertTitle>
                  <AlertDescription>
                    <Button
                      type="button"
                      variant="outline"
                      disabled={locked}
                      loading={options.isFetching}
                      onClick={() => {
                        void options.refetch();
                      }}
                    >
                      {t('common.retry')}
                    </Button>
                  </AlertDescription>
                </Alert>
              ) : null}
            </div>
            <div className="space-y-1">
              <Label htmlFor="ai-content-body">
                {t('aiContent.fields.content')}
                <RequiredMark />
              </Label>
              <Textarea
                id="ai-content-body"
                aria-required="true"
                required
                value={draft.content}
                rows={12}
                maxLength={100000}
                disabled={locked}
                aria-invalid={invalid.content || undefined}
                aria-describedby={invalid.content ? 'ai-content-body-error' : undefined}
                onChange={(e) => {
                  setDraft({ ...draft, content: e.target.value });
                  setInvalid({ ...invalid, content: false });
                }}
              />
              {fieldErrors.content ? (
                <Text id="ai-content-body-error" role="alert" size="sm" color="destructive">
                  {fieldErrors.content}
                </Text>
              ) : null}
            </div>
            {feedback ? (
              <Alert
                id="ai-content-feedback"
                variant={feedback === 'error' ? 'destructive' : 'default'}
              >
                <AlertTitle>
                  {feedback === 'saved'
                    ? t('aiContent.save.readPending')
                    : feedback === 'unknown'
                      ? t('aiContent.save.unknown')
                      : feedback === 'latest'
                        ? t('aiContent.conflict.review')
                        : feedback === 'permission'
                          ? t('aiContent.errors.denied')
                          : t(errorKey(error))}
                </AlertTitle>
                <AlertDescription className="space-y-3">
                  {uncertain ? <Text>{t('aiContent.save.sameRetry')}</Text> : null}
                  {latest ? (
                    <>
                      <Text>
                        {t(
                          actualChange
                            ? 'aiContent.conflict.changed'
                            : 'aiContent.conflict.metadata'
                        )}
                      </Text>
                      <DescriptionList
                        columns={1}
                        items={[
                          {
                            id: 'title',
                            term: t('aiContent.fields.title'),
                            description: latest.data.title ?? t('crm.missingValue'),
                          },
                          {
                            id: 'type',
                            term: t('aiContent.fields.type'),
                            description: aiContentTypeLabel(
                              latest.data.type,
                              options.data,
                              t('crm.missingValue')
                            ),
                          },
                          {
                            id: 'content',
                            term: t('aiContent.fields.content'),
                            description: (
                              <div className="max-h-64 overflow-auto whitespace-pre-wrap break-words">
                                {latest.data.content ?? t('crm.missingValue')}
                              </div>
                            ),
                          },
                        ]}
                      />
                      <div className="grid auto-rows-fr gap-2 sm:flex sm:flex-wrap">
                        <Button
                          className="h-auto min-h-11 w-full whitespace-normal sm:w-auto"
                          type="button"
                          variant="outline"
                          onClick={() => {
                            chooseLatest(false);
                          }}
                        >
                          {t('aiContent.conflict.useLatest')}
                        </Button>
                        <Button
                          className="h-auto min-h-11 w-full whitespace-normal sm:w-auto"
                          type="button"
                          onClick={() => {
                            chooseLatest(true);
                          }}
                        >
                          {t('aiContent.conflict.keepDraft')}
                        </Button>
                      </div>
                    </>
                  ) : conflict && !uncertain ? (
                    <>
                      <Text>{t('aiContent.conflict.explanation')}</Text>
                      <Button
                        className="h-auto min-h-11 w-full whitespace-normal sm:w-auto"
                        type="button"
                        variant="outline"
                        loading={loadingLatest}
                        onClick={() => {
                          void refresh();
                        }}
                      >
                        {t('aiContent.conflict.load')}
                      </Button>
                    </>
                  ) : null}
                  {readError ? <Text>{t(errorKey(readError))}</Text> : null}
                </AlertDescription>
              </Alert>
            ) : null}
          </CardContent>
          <CardFooter className="flex-wrap justify-end gap-2 p-4 pt-0">
            <Button
              type="button"
              variant="outline"
              disabled={busy || Boolean(receipt)}
              onClick={() => {
                if (dirty || uncertain) setCancelOpen(true);
                else cancel();
              }}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              loading={submitting || mutation.isPending || (Boolean(receipt) && loadingLatest)}
              disabled={
                busy ||
                (!receipt &&
                  (!canUpdate || (!uncertain && (denied || Boolean(latest) || conflict || !dirty))))
              }
            >
              {t(
                receipt
                  ? 'aiContent.save.reload'
                  : uncertain
                    ? 'aiContent.save.retrySame'
                    : 'common.save'
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title={t('aiContent.unsaved.title')}
        description={t(uncertain ? 'aiContent.unsaved.unknown' : 'aiContent.unsaved.description')}
        confirmLabel={t('aiContent.unsaved.leave')}
        cancelLabel={t('aiContent.unsaved.stay')}
        onConfirm={cancel}
      />
      <ConfirmDialog
        open={blocker.status === 'blocked'}
        confirmButtonProps={{
          disabled: mutation.isPending || loadingLatest,
        }}
        onOpenChange={(open) => {
          if (!open && blocker.status === 'blocked') blocker.reset();
        }}
        title={t('aiContent.unsaved.title')}
        description={t(uncertain ? 'aiContent.unsaved.unknown' : 'aiContent.unsaved.description')}
        confirmLabel={t('aiContent.unsaved.leave')}
        cancelLabel={t('aiContent.unsaved.stay')}
        onConfirm={() => {
          if (blocker.status === 'blocked' && !saving.current && !loadingLatest) {
            allowLeave.current = true;
            blocker.proceed();
          }
        }}
      />
    </section>
  );
}
